import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  Send,
  Copy,
  Check,
  Trash2,
  Mail,
  ExternalLink,
  Search,
  Loader2,
  Eye,
  X,
  RotateCcw,
  ShieldCheck,
  ClipboardList,
  Inbox,
  Settings2,
} from 'lucide-react';
import type { User } from 'firebase/auth';
import {
  FORM_TYPES,
  FORMS_BASE_URL,
  DEFAULT_INSTRUCTIONS,
  formatFormType,
  onFormsAuthChanged,
  bridgeFormsAuthWithToken,
  connectFormsWithPopup,
  subscribeInvites,
  subscribeApplications,
  subscribeFormTemplates,
  saveFormTemplate,
  resetFormTemplate,
  createFormInvite,
  deleteFormInvite,
  sendInviteEmail,
  type FormInvite,
  type FormApplication,
  type RateAgreementLetterFields,
} from '../lib/formsDb';
import type { Lead } from '../types';
import { FORM_VARIABLE_GROUPS } from '../lib/templateEngine';

export type FormsSubTab = 'send' | 'invites' | 'submissions' | 'templates';

interface FormsHubProps {
  subTab: FormsSubTab;
  onSubTabChange: (tab: FormsSubTab) => void;
  onNotify?: (title: string, body: string) => void;
  leads?: Lead[];
}

const EMPTY_RA_FIELDS: RateAgreementLetterFields = {
  letterDate: new Date().toISOString().split('T')[0],
  billingEmail: '', accountNumber: '', address: '', cityStateZip: '',
  originCityState: '', destinationCityState: '', firstShipmentDate: '',
  palletRate: '', cubeRateDetails: '', minimumShipmentDetails: '',
  paymentTerms: '', paymentTimeframe: '', lateFeePercentage: '',
  senderEmail: '', senderName: '',
};

const RA_FIELD_LABELS: { key: keyof RateAgreementLetterFields; label: string; type?: string }[] = [
  { key: 'letterDate', label: 'Letter Date', type: 'date' },
  { key: 'accountNumber', label: 'Account Number' },
  { key: 'billingEmail', label: 'Billing Email', type: 'email' },
  { key: 'address', label: 'Address' },
  { key: 'cityStateZip', label: 'City, State, Zip' },
  { key: 'originCityState', label: 'Origin (City, State)' },
  { key: 'destinationCityState', label: 'Destination (City, State)' },
  { key: 'firstShipmentDate', label: 'First Shipment Date', type: 'date' },
  { key: 'palletRate', label: 'Pallet Rate' },
  { key: 'cubeRateDetails', label: 'Cube Rate Details' },
  { key: 'minimumShipmentDetails', label: 'Minimum Shipment Details' },
  { key: 'paymentTerms', label: 'Payment Terms' },
  { key: 'paymentTimeframe', label: 'Payment Timeframe' },
  { key: 'lateFeePercentage', label: 'Late Fee %' },
  { key: 'senderName', label: 'Sender Name' },
  { key: 'senderEmail', label: 'Sender Email', type: 'email' },
];

const STATUS_STYLES: Record<string, string> = {
  sent: 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900',
  opened: 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900',
  completed: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-900',
  pending: 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${STATUS_STYLES[status] || 'bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800'}`}>
      {status}
    </span>
  );
}

function tsToDate(ts: any): string {
  try {
    if (!ts) return '—';
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return '—';
  }
}

const inputCls = "w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition";
const labelCls = "block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1";

export default function FormsHub({ subTab, onSubTabChange, onNotify, leads }: FormsHubProps) {
  const [formsUser, setFormsUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const [invites, setInvites] = useState<FormInvite[]>([]);
  const [applications, setApplications] = useState<FormApplication[]>([]);
  const [templates, setTemplates] = useState<Record<string, { instructions: string }>>({});

  // Send form state
  const [selectedLeadId, setSelectedLeadId] = useState<string>('');
  const [draft, setDraft] = useState({ companyName: '', signerName: '', email: '', formType: 'credit_application' });
  const [raFields, setRaFields] = useState<RateAgreementLetterFields>({ ...EMPTY_RA_FIELDS });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ ok: boolean; message: string } | null>(null);

  const handleSelectLeadToPrefill = (leadId: string) => {
    setSelectedLeadId(leadId);
    if (!leadId) return;
    const lead = (leads || []).find((l) => l.id === leadId);
    if (!lead) return;

    setDraft((prev) => ({
      ...prev,
      companyName: lead.company || prev.companyName,
      signerName: lead.contactPerson || lead.name || prev.signerName,
      email: lead.email || prev.email,
    }));

    setRaFields({
      letterDate: new Date().toISOString().split('T')[0],
      accountNumber: lead.accountNumber || '',
      billingEmail: lead.email || '',
      address: lead.street || '',
      cityStateZip: [lead.city, lead.state, lead.zipCode].filter(Boolean).join(', '),
      originCityState: (lead.outboundWarehouses && lead.outboundWarehouses.length > 0) ? `${lead.outboundWarehouses.join(', ')}, FL` : 'Miami, FL',
      destinationCityState: [lead.city, lead.state].filter(Boolean).join(', '),
      firstShipmentDate: lead.firstShipmentDate || '',
      palletRate: lead.quoteDollarAmount ? `$${lead.quoteDollarAmount}` : '',
      cubeRateDetails: lead.quoteDetails || '',
      minimumShipmentDetails: lead.fullQuoteView || '',
      paymentTerms: 'Net 15',
      paymentTimeframe: '15 Days',
      lateFeePercentage: '1.5%',
      senderName: lead.owner || 'Mark Sterling',
      senderEmail: 'msterling@armellini.com',
    });
  };

  // Lists state
  const [inviteSearch, setInviteSearch] = useState('');
  const [appSearch, setAppSearch] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<FormApplication | null>(null);

  // Templates state
  const [editingType, setEditingType] = useState<string | null>(null);
  const [templateDraft, setTemplateDraft] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    let disposed = false;
    const unsub = onFormsAuthChanged(async (u) => {
      if (!u) {
        // No forms session yet — silently reuse the CRM's Google token before
        // showing anything. If it works, this listener fires again with a user.
        const token = localStorage.getItem('google_access_token');
        if (token && (await bridgeFormsAuthWithToken(token))) return;
      }
      if (disposed) return;
      setFormsUser(u);
      setAuthChecked(true);
    });
    return () => { disposed = true; unsub(); };
  }, []);

  useEffect(() => {
    if (!formsUser) return;
    const u1 = subscribeInvites(setInvites);
    const u2 = subscribeApplications(setApplications);
    const u3 = subscribeFormTemplates(setTemplates);
    return () => { u1(); u2(); u3(); };
  }, [formsUser?.uid]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await connectFormsWithPopup();
    } catch (e: any) {
      alert(`Could not connect to the forms workspace: ${e.message || 'Unknown error'}`);
    } finally {
      setConnecting(false);
    }
  };

  const instructionsFor = (formType: string) =>
    templates[formType]?.instructions ?? DEFAULT_INSTRUCTIONS[formType] ?? '';

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setSendResult(null);
    try {
      const { inviteLink } = await createFormInvite({
        companyName: draft.companyName,
        signerName: draft.signerName,
        customerEmail: draft.email,
        formType: draft.formType,
        rateAgreementFields: draft.formType === 'rate_agreement_letter' ? raFields : undefined,
      });
      try {
        await sendInviteEmail({
          to: draft.email,
          companyName: draft.companyName,
          signerName: draft.signerName,
          inviteLink,
          instructions: instructionsFor(draft.formType),
          formTitle: formatFormType(draft.formType),
        });
        setSendResult({ ok: true, message: `Invite emailed to ${draft.email}.` });
        onNotify?.('Form invite sent', `${formatFormType(draft.formType)} sent to ${draft.signerName || draft.email} (${draft.companyName}).`);
      } catch (emailErr: any) {
        try { await navigator.clipboard.writeText(inviteLink); } catch { /* clipboard unavailable */ }
        setSendResult({ ok: false, message: `Invite created but the email failed (${emailErr.message}). The link was copied to your clipboard.` });
      }
      setDraft({ companyName: '', signerName: '', email: '', formType: draft.formType });
      setRaFields({ ...EMPTY_RA_FIELDS });
    } catch (err: any) {
      setSendResult({ ok: false, message: `Failed to create invite: ${err.message || 'Unknown error'}` });
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (invite: FormInvite) => {
    setResendingId(invite.id);
    try {
      await sendInviteEmail({
        to: invite.customerEmail,
        companyName: invite.companyName,
        signerName: invite.signerName,
        inviteLink: `${FORMS_BASE_URL}/apply/${invite.id}`,
        instructions: instructionsFor(invite.formType),
        formTitle: formatFormType(invite.formType),
      });
      onNotify?.('Invite re-sent', `${formatFormType(invite.formType)} re-sent to ${invite.customerEmail}.`);
    } catch (e: any) {
      alert(`Resend failed: ${e.message || 'Unknown error'}`);
    } finally {
      setResendingId(null);
    }
  };

  const handleCopyLink = (inviteId: string) => {
    navigator.clipboard.writeText(`${FORMS_BASE_URL}/apply/${inviteId}`);
    setCopiedId(inviteId);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleDeleteInvite = async (id: string) => {
    setDeleteConfirmId(null);
    try {
      await deleteFormInvite(id);
    } catch (e: any) {
      alert(`Delete failed: ${e.message || 'Unknown error'}`);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingType) return;
    setSavingTemplate(true);
    try {
      await saveFormTemplate(editingType, templateDraft);
      setEditingType(null);
    } catch (e: any) {
      alert(`Save failed: ${e.message || 'Unknown error'}`);
    } finally {
      setSavingTemplate(false);
    }
  };

  const filteredInvites = useMemo(() => {
    const q = inviteSearch.toLowerCase();
    return invites.filter(i =>
      !q ||
      i.companyName?.toLowerCase().includes(q) ||
      i.signerName?.toLowerCase().includes(q) ||
      i.customerEmail?.toLowerCase().includes(q) ||
      formatFormType(i.formType).toLowerCase().includes(q)
    );
  }, [invites, inviteSearch]);

  const filteredApps = useMemo(() => {
    const q = appSearch.toLowerCase();
    return applications.filter(a =>
      !q ||
      a.customerName?.toLowerCase().includes(q) ||
      a.customerEmail?.toLowerCase().includes(q) ||
      formatFormType(a.formData?.formType || '').toLowerCase().includes(q)
    );
  }, [applications, appSearch]);

  const SUB_TABS: { id: FormsSubTab; label: string; icon: any }[] = [
    { id: 'send', label: 'Send a Form', icon: Send },
    { id: 'invites', label: `Invites (${invites.length})`, icon: ClipboardList },
    { id: 'submissions', label: `Submissions (${applications.length})`, icon: Inbox },
    { id: 'templates', label: 'Email Templates', icon: Settings2 },
  ];

  // ------------------------------------------------------------------
  // Connect gate — the forms backend is a separate Firebase project, so
  // it needs its own (one-time) Google session in this browser.
  // ------------------------------------------------------------------
  if (authChecked && !formsUser) {
    return (
      <div className="p-6 md:p-8 max-w-2xl mx-auto">
        <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-xl bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h2 className="mt-4 text-lg font-black text-slate-900 dark:text-slate-100 tracking-tight">Refresh Forms Access</h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            Your session with the secure forms backend (<span className="font-mono text-xs">forms.armellini.com</span>) has
            expired and could not be refreshed automatically. Click below to restore it — since you're already signed
            into Google, this completes instantly without re-entering credentials.
          </p>
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="mt-6 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition shadow-lg shadow-blue-500/25 cursor-pointer"
          >
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {connecting ? 'Refreshing…' : 'Refresh Access'}
          </button>
        </div>
      </div>
    );
  }

  if (!authChecked) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> <span className="text-sm font-semibold">Loading forms workspace…</span>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Module header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-black text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" /> Client Forms & Applications
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Send onboarding forms to prospective clients and track their submissions — powered by forms.armellini.com.
          </p>
        </div>
        <a
          href={FORMS_BASE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
        >
          Open Forms Console <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Sub-tab bar */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-800">
        {SUB_TABS.map((t) => {
          const Icon = t.icon;
          const active = subTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onSubTabChange(t.id)}
              className={`flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-bold border-b-2 -mb-px transition-colors cursor-pointer ${
                active
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ------------------------------------------------------------ */}
      {/* SEND A FORM                                                   */}
      {/* ------------------------------------------------------------ */}
      {subTab === 'send' && (
        <form onSubmit={handleSendInvite} className="max-w-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm p-5 md:p-6 space-y-4">
          {leads && leads.length > 0 && (
            <div className="bg-blue-50/70 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/60 rounded-xl p-3.5">
              <label className="block text-[11px] font-extrabold text-blue-700 dark:text-blue-300 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" /> Pre-fill Form Variables from Lead Details Form
              </label>
              <select
                value={selectedLeadId}
                onChange={(e) => handleSelectLeadToPrefill(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-blue-300 dark:border-blue-700 rounded-lg text-xs font-bold text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-xs cursor-pointer"
              >
                <option value="">-- Choose a Lead from Details Form to Auto-Fill Fields --</option>
                {leads.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.company || l.name} ({l.contactPerson || l.name} - {l.email || 'No Email'})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Form Type</label>
              <select
                value={draft.formType}
                onChange={(e) => setDraft({ ...draft, formType: e.target.value })}
                className={inputCls}
              >
                {FORM_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Company Name *</label>
              <input required value={draft.companyName} onChange={(e) => setDraft({ ...draft, companyName: e.target.value })} className={inputCls} placeholder="e.g. Sunshine Blooms LLC" />
            </div>
            <div>
              <label className={labelCls}>Signer / Contact Name *</label>
              <input required value={draft.signerName} onChange={(e) => setDraft({ ...draft, signerName: e.target.value })} className={inputCls} placeholder="e.g. Jordan Rivera" />
            </div>
            <div>
              <label className={labelCls}>Customer Email *</label>
              <input required type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className={inputCls} placeholder="customer@company.com" />
            </div>
          </div>

          {draft.formType === 'rate_agreement_letter' && (
            <div className="pt-2">
              <div className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Rate Agreement Details (pre-filled into the letter)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4">
                {RA_FIELD_LABELS.map((f) => (
                  <div key={f.key}>
                    <label className={labelCls}>{f.label}</label>
                    <input
                      type={f.type || 'text'}
                      value={raFields[f.key]}
                      onChange={(e) => setRaFields({ ...raFields, [f.key]: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-1 border-t border-slate-100 dark:border-slate-900">
            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-3">
              The customer receives an email (from the Armellini Customer Center) with a secure link to complete the
              {' '}{formatFormType(draft.formType)} at forms.armellini.com. Email wording is configurable under <b>Email Templates</b>.
            </p>
            <div className="mt-3 flex items-center gap-3">
              <button
                type="submit"
                disabled={sending}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition shadow-lg shadow-blue-500/25 cursor-pointer"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending…' : 'Create & Email Invite'}
              </button>
              {sendResult && (
                <span className={`text-xs font-semibold ${sendResult.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {sendResult.message}
                </span>
              )}
            </div>
          </div>
        </form>
      )}

      {/* ------------------------------------------------------------ */}
      {/* INVITES                                                       */}
      {/* ------------------------------------------------------------ */}
      {subTab === 'invites' && (
        <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
          <div className="p-3 border-b border-slate-100 dark:border-slate-900 flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={inviteSearch}
                onChange={(e) => setInviteSearch(e.target.value)}
                placeholder="Search invites…"
                className={`${inputCls} pl-8 py-1.5 text-xs`}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-900">
                  {['Company', 'Signer', 'Email', 'Form', 'Status', 'Sent', ''].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredInvites.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-50 dark:border-slate-900/60 hover:bg-slate-50/60 dark:hover:bg-slate-900/40 transition-colors">
                    <td className="px-4 py-2.5 text-sm font-bold text-slate-800 dark:text-slate-100">{inv.companyName}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300">{inv.signerName}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400 font-mono">{inv.customerEmail}</td>
                    <td className="px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300">{formatFormType(inv.formType)}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={inv.status} /></td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{tsToDate(inv.createdAt)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button title="Copy link" onClick={() => handleCopyLink(inv.id)} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition cursor-pointer">
                          {copiedId === inv.id ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                        <button title="Resend email" onClick={() => handleResend(inv)} disabled={resendingId === inv.id} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition cursor-pointer disabled:opacity-50">
                          {resendingId === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                        </button>
                        {deleteConfirmId === inv.id ? (
                          <span className="flex items-center gap-1">
                            <button onClick={() => handleDeleteInvite(inv.id)} className="text-[10px] font-bold text-red-600 px-1.5 py-1 rounded hover:bg-red-50 dark:hover:bg-red-950/40 cursor-pointer">Confirm</button>
                            <button onClick={() => setDeleteConfirmId(null)} className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer"><X className="w-3 h-3" /></button>
                          </span>
                        ) : (
                          <button title="Delete invite" onClick={() => setDeleteConfirmId(inv.id)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition cursor-pointer">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredInvites.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400">No invites {inviteSearch ? 'match your search' : 'sent yet'}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------ */}
      {/* SUBMISSIONS                                                   */}
      {/* ------------------------------------------------------------ */}
      {subTab === 'submissions' && (
        <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
          <div className="p-3 border-b border-slate-100 dark:border-slate-900 flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={appSearch}
                onChange={(e) => setAppSearch(e.target.value)}
                placeholder="Search submissions…"
                className={`${inputCls} pl-8 py-1.5 text-xs`}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-900">
                  {['Customer', 'Email', 'Form', 'Status', 'Submitted', ''].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredApps.map((app) => (
                  <tr key={app.id} className="border-b border-slate-50 dark:border-slate-900/60 hover:bg-slate-50/60 dark:hover:bg-slate-900/40 transition-colors">
                    <td className="px-4 py-2.5 text-sm font-bold text-slate-800 dark:text-slate-100">{app.customerName}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400 font-mono">{app.customerEmail}</td>
                    <td className="px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300">{formatFormType(app.formData?.formType || '')}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={app.status} /></td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{tsToDate(app.submittedAt)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => setSelectedApp(app)} className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredApps.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">No submissions {appSearch ? 'match your search' : 'yet'}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------ */}
      {/* EMAIL TEMPLATES                                               */}
      {/* ------------------------------------------------------------ */}
      {subTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl">
          {FORM_TYPES.map((t) => {
            const isEditing = editingType === t.id;
            const current = instructionsFor(t.id);
            const customized = !!templates[t.id];
            return (
              <div key={t.id} className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm p-4 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">{t.label}</h3>
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${customized ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900' : 'bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-800'}`}>
                    {customized ? 'Customized' : 'Default'}
                  </span>
                </div>
                {isEditing ? (
                  <>
                    <textarea
                      value={templateDraft}
                      onChange={(e) => setTemplateDraft(e.target.value)}
                      rows={8}
                      className={`${inputCls} font-mono text-xs leading-relaxed resize-y`}
                    />
                    <div className="mt-3 flex items-center gap-2">
                      <button onClick={handleSaveTemplate} disabled={savingTemplate} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold px-3 py-1.5 rounded-lg text-xs transition cursor-pointer">
                        {savingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
                      </button>
                      <button onClick={() => setEditingType(null)} className="text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 px-2 py-1.5 cursor-pointer">Cancel</button>
                      {customized && (
                        <button
                          onClick={async () => { await resetFormTemplate(t.id); setEditingType(null); }}
                          className="ml-auto flex items-center gap-1 text-xs font-bold text-amber-600 dark:text-amber-400 hover:underline cursor-pointer"
                        >
                          <RotateCcw className="w-3 h-3" /> Reset to default
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <pre className="flex-1 text-xs text-slate-500 dark:text-slate-400 whitespace-pre-wrap font-sans bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-lg p-3 max-h-40 overflow-y-auto">{current}</pre>
                    <button
                      onClick={() => { setEditingType(t.id); setTemplateDraft(current); }}
                      className="mt-3 self-start text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                    >
                      Edit instructions
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ------------------------------------------------------------ */}
      {/* SUBMISSION DETAIL SLIDE-OVER                                  */}
      {/* ------------------------------------------------------------ */}
      {selectedApp && (
        <div className="fixed inset-0 z-[100] overflow-hidden">
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setSelectedApp(null)} />
          <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-white dark:bg-slate-950 border-l border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
              <div>
                <h3 className="text-sm font-black text-slate-900 dark:text-slate-100">{selectedApp.customerName}</h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                  {formatFormType(selectedApp.formData?.formType || '')} · {tsToDate(selectedApp.submittedAt)}
                </p>
              </div>
              <button onClick={() => setSelectedApp(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-800 transition cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <SubmissionDetail app={selectedApp} />
            </div>
            <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex items-center justify-between">
              <span className="text-[11px] text-slate-400">Full PDF download is available in the Forms Console.</span>
              <a
                href={FORMS_BASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline"
              >
                Open Forms Console <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Submission detail renderer — formats each form type's data as label/value
// rows, masking sensitive card data.
// ---------------------------------------------------------------------------

function DetailSection({ title, rows }: { title: string; rows: [string, any][] }) {
  const visible = rows.filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (visible.length === 0) return null;
  return (
    <div className="bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-lg p-4">
      <h4 className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400 mb-2.5">{title}</h4>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
        {visible.map(([label, value]) => (
          <div key={label}>
            <dt className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wide">{label}</dt>
            <dd className="text-xs text-slate-700 dark:text-slate-200 font-medium break-words">{String(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function DriveLinks({ app }: { app: FormApplication }) {
  const links = [
    { url: app.einDriveUrl, label: app.einFileName || 'EIN / Resale Certificate' },
    { url: app.w9DriveUrl, label: app.w9FileName || 'W-9 Document' },
    { url: app.signedAgreementDriveUrl, label: 'Signed Agreement' },
  ].filter((l) => l.url);
  if (links.length === 0) return null;
  return (
    <div className="bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-lg p-4">
      <h4 className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400 mb-2.5">Attached Documents</h4>
      <div className="space-y-1.5">
        {links.map((l) => (
          <a key={l.url as string} href={l.url as string} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline">
            <FileText className="w-3.5 h-3.5" /> {l.label} <ExternalLink className="w-3 h-3" />
          </a>
        ))}
      </div>
    </div>
  );
}

function SubmissionDetail({ app }: { app: FormApplication }) {
  const fd = app.formData || {};
  const type = fd.formType;

  if (type === 'credit_application') {
    const g = fd.general || {};
    const s = fd.shipping || {};
    const a = fd.agreements || {};
    const days = s.days ? Object.entries(s.days).filter(([, v]) => v).map(([d]) => d).join(', ') : '';
    return (
      <>
        <DetailSection title="Company" rows={[
          ['Legal Name', g.legalName], ['DBA', g.dba], ['Company Type', g.companyType],
          ['Federal Tax ID', g.fedTaxId], ['Phone', g.phone],
          ['Address', [g.address, g.city, g.state, g.zip].filter(Boolean).join(', ')],
          ['Invoice To', g.invoiceTo],
        ]} />
        <DetailSection title="Contacts" rows={[
          ['Buyer', g.buyerName], ['Buyer Phone', g.buyerPhone], ['Buyer Email', g.buyerEmail],
          ['A/P Contact', g.apName], ['A/P Email', g.apEmail], ['A/P Phone', g.apPhone],
        ]} />
        {(fd.tradeReferences || []).map((r: any, i: number) => (
          <React.Fragment key={i}>
            <DetailSection title={`Trade Reference ${i + 1}`} rows={[['Name', r.name], ['Phone', r.phone], ['Email', r.email]]} />
          </React.Fragment>
        ))}
        <DetailSection title="Shipping" rows={[
          ['Carrier', s.carrier], ['Account', s.account], ['Delivery Days', days],
          ['Contact', s.contactName], ['Email', s.email], ['Receiving Hours', s.receivingHours],
        ]} />
        <DetailSection title="Agreements" rows={[
          ['Claim Policy Accepted', a.claimPolicyAccepted ? 'Yes' : 'No'],
          ['Security Agreement Accepted', a.securityAgreementAccepted ? 'Yes' : 'No'],
          ['Signed Date', a.signDate],
        ]} />
        <DriveLinks app={app} />
      </>
    );
  }

  if (type === 'w9') {
    const t = fd.taxpayer || {};
    const tin = fd.tin || {};
    const c = fd.certification || {};
    return (
      <>
        <DetailSection title="Taxpayer" rows={[
          ['Name', t.name], ['Business Name', t.businessName], ['Tax Classification', t.taxClassification],
          ['Address', [t.address, t.city, t.state, t.zip].filter(Boolean).join(', ')],
        ]} />
        <DetailSection title="TIN" rows={[
          ['Type', tin.tinType?.toUpperCase()],
          ['Number', tin.tin ? `•••••${String(tin.tin).slice(-4)}` : ''],
        ]} />
        <DetailSection title="Certification" rows={[
          ['Accepted', c.certificationAccepted ? 'Yes' : 'No'], ['Signed Date', c.signedDate],
        ]} />
        <DriveLinks app={app} />
      </>
    );
  }

  if (type === 'credit_card_authorization') {
    const card = fd.card || {};
    const auth = fd.authorization || {};
    return (
      <>
        <DetailSection title="Cardholder" rows={[
          ['Name', [card.cardHolderFirstName, card.cardHolderLastName].filter(Boolean).join(' ')],
          ['Company', card.companyName], ['Phone', card.telephone],
          ['Billing Address', [card.billingAddress, card.city, card.state, card.zipCode].filter(Boolean).join(', ')],
        ]} />
        <DetailSection title="Card (masked)" rows={[
          ['Card Type', card.cardType?.toUpperCase()],
          ['Card Number', card.cardNumber ? `•••• •••• •••• ${String(card.cardNumber).replace(/\s/g, '').slice(-4)}` : ''],
          ['Expiration', card.expMonth && card.expYear ? `${card.expMonth}/${card.expYear}` : ''],
          ['Invoice / Order #', card.invoiceOrderNumber], ['Amount', card.totalAmount],
        ]} />
        <DetailSection title="Authorization" rows={[
          ['Valid Until', auth.validUntilMonth && auth.validUntilYear ? `${auth.validUntilMonth}/${auth.validUntilYear}` : ''],
          ['Policy Accepted', auth.policyAccepted ? 'Yes' : 'No'], ['Signed Date', auth.signedDate],
        ]} />
        <DriveLinks app={app} />
      </>
    );
  }

  if (type === 'rate_agreement_letter') {
    const f = fd.fields || {};
    return (
      <>
        <DetailSection title="Agreement Terms" rows={[
          ['Letter Date', f.letterDate], ['Account #', f.accountNumber], ['Billing Email', f.billingEmail],
          ['Origin', f.originCityState], ['Destination', f.destinationCityState],
          ['First Shipment', f.firstShipmentDate], ['Pallet Rate', f.palletRate],
          ['Cube Rate', f.cubeRateDetails], ['Minimum Shipment', f.minimumShipmentDetails],
          ['Payment Terms', f.paymentTerms], ['Payment Timeframe', f.paymentTimeframe],
          ['Late Fee %', f.lateFeePercentage],
        ]} />
        <DetailSection title="Signature" rows={[
          ['Print Name', fd.printName], ['Accepted', fd.agreementAccepted ? 'Yes' : 'No'], ['Signed Date', fd.signedDate],
        ]} />
        <DriveLinks app={app} />
      </>
    );
  }

  // Unknown/legacy form type — dump top-level scalars so nothing is hidden
  return (
    <>
      <DetailSection
        title="Submission Data"
        rows={Object.entries(fd).filter(([, v]) => typeof v !== 'object').map(([k, v]) => [k, v] as [string, any])}
      />
      <DriveLinks app={app} />
    </>
  );
}
